import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CopilotChatReference, FigmaReference} from '../utils/copilot-chat-types'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../utils/reference-mention'

const figmaUrlPattern = /^(?:https:\/\/)?(?:www\.)?(figma\.com\/.+)$/

class FigmaNotAuthorizedError extends Error {
  constructor() {
    super()
    this.name = 'FigmaNotAuthorizedError'
  }
}

export function useAddUrlReferences({figmaAuthUrl}: {figmaAuthUrl?: string}) {
  const manager = useChatManager()

  // This is probably a misuse of `useMutation`, but it's a decent compromise that allows us to fetch imperatively.
  // It does unfortunately mean that we don't get any caching, since mutations aren't cached.

  const githubReferenceQuery = useMutation({
    mutationFn: async (itemUrl: string) => {
      const response = await verifiedFetchJSON(`/copilot/chat-links?item_url=${itemUrl}`)
      if (!response.ok) throw new Error(`Failed to fetch reference data (${response.status}: ${response.statusText})`)
      return (await response.json()) as CopilotChatReference
    },
    onSuccess: reference => manager.addReference(reference, 'chatInput'),
  })

  const figmaReferenceQuery = useMutation({
    mutationFn: async (itemUrl: string) => {
      const response = await verifiedFetchJSON(`/copilot/immersive/figma-link?item_url=${itemUrl}`)
      if (!response.ok) {
        if (response.status === 403) throw new FigmaNotAuthorizedError()
        throw new Error(`Failed to fetch Figma reference data (${response.status}: ${response.statusText})`)
      }
      return (await response.json()) as FigmaReference
    },
    onSuccess: reference => manager.addReference(reference, 'chatInput'),
    onError: (error, itemUrl) => {
      // for figma we create this fake reference to give the user a link to initiate oauth with figma
      // would be nice if this could be more general
      if (figmaAuthUrl && error instanceof FigmaNotAuthorizedError)
        manager.addReference(
          {
            type: 'figma',
            title: 'Sign in to Figma',
            id: `forbidden-${itemUrl}`,
            url: figmaAuthUrl,
            thumbnailUrl: '',
            fullImageUrl: '',
            authenticationRequired: true,
          },
          'chatInput',
        )
    },
  })

  /**
   * If the passed text is a valid reference URL, fetches reference details and adds the reference to chat state.
   * @returns Transformed text to replace the URL, such as a reference mention.
   */
  const addReferenceForUrl = (text: string) => {
    // We only do reference processing if the entire pasted text is a single URL
    if (/\s/.test(text)) return text

    const parsedReferenceMention = ReferenceMention.fromUrl(text)
    if (parsedReferenceMention) {
      githubReferenceQuery.mutate(text)
      text = ReferenceMention.stringify(parsedReferenceMention)
    }

    const figmaReferenceMatch = figmaUrlPattern.exec(text)
    if (figmaReferenceMatch) {
      figmaReferenceQuery.mutate(text)
      text = figmaReferenceMatch[1]!
    }

    return text
  }

  return {
    isLoading: githubReferenceQuery.isPending || figmaReferenceQuery.isPending,
    addReferenceForUrl,
  }
}
